import { Injectable } from '@angular/core';
import { SocketConnections, StreamTypes, SocketCommands, SockRoutes, SocketErrors, MarketStreams, SocketErrorCodes } from 'sharedweb/app.structs';
import { Subject } from 'rxjs';
import { WebSocketBridge } from 'django-channels';
import {
    SocketMessageQueue, SocketGenericMessage, SocketError, SocketAuthResponse, SocketAction,
    MarginSummaryResponse
} from 'sharedweb/app.interface';
import { HttpClient } from '@angular/common/http';
import { applicationUrls } from 'sharedweb/helpers/urls';
import { StorageHelper } from 'sharedweb/helpers/storage';
import { EventsService } from '@event-service';
import { IONIC_EVENTS, MARGIN_SUMMARY_INTERVAL } from 'sharedweb/helpers/shared-constants';
import { MarginSummaryService } from '../margin-summary/margin-summary.service';
import { ConfigParamsService } from '../config-params/config-params.service';
import { EVENTS } from 'src/app/_helpers';
import { delay, retryWhen } from 'rxjs/operators';
import { WebsocketMessages } from 'sharedweb/websocket.enums';

@Injectable({
    providedIn: 'root'
})
export class SocketService {

    private webSocketBridges: Map<SocketConnections, WebSocketBridge> = new Map();
    private streams: Map<SocketConnections, Map<StreamTypes, Subject<any>>> = new Map();
    private channelObservers: Map<SocketConnections, Subject<any>> = new Map();

    private connectionQueueMap: Map<SocketConnections, SocketMessageQueue> = new Map();
    private routes: { [key: string]: Subject<any> } = {};
    private lastSubscriptionMap: Map<SocketConnections, any[]> = new Map();

    private firstConnection = true;

    private storageHelper: StorageHelper;
    interval: NodeJS.Timer;
    marginSubject = new Subject<MarginSummaryResponse[]>();
    intervalSet: number;
    marginCallSubscribe = 0;

    private lastPongTime: number;
    private lastPingTime: number;
    private websocketTimeout = 5000;
    private observerInterval;

    constructor(
        private httpClient: HttpClient,
        private _eventService: EventsService,
        private cacheService: ConfigParamsService,
        private _marginService: MarginSummaryService,
    ) {
        Object.values(SocketConnections).forEach(connection => {
            this.lastSubscriptionMap.set(connection, []);
        });
    }

    private _routeSubject(route: SockRoutes): Subject<any> {
        // let sockRoute = this._routes[route];
        if (!this.routes[route]) {
            this.routes[route] = new Subject<any>();
        }
        return this.routes[route];
    }

    authenticateSocket() {
        const url = applicationUrls.socketAuthUrl;
        return this.httpClient.get<SocketAuthResponse>(url)
            .pipe(
                // Will continue to retry fetching websocket token in case of some error.
                // This will most likly occur in case of network disconnect or webserver
                // restart.
                retryWhen(e => {
                    return e.pipe(delay(this.websocketTimeout / 2));
                })
            ).toPromise();
    }


    observeWebsocketDisconnect(webSocketBridge) {

        // If there was already a interval running, stop that before starting a new one.
        // This will happen in case of reconnection.
        if (this.observerInterval) {
            clearInterval(this.observerInterval);
        }

        // Will be checking connection status every half time-out - 1sec (1sec to compensate for network transfer time).
        this.observerInterval = setInterval(() => {
            // If the last ping time was more than the expected timeout, disconnect and reconnect
            // socket

            if ((this.lastPingTime - this.lastPongTime) > this.websocketTimeout) {
                console.error(`STALE WEBSOCKET CONNECTION... Reconnecting  LastPingTime ${this.lastPingTime} LastPongTime ${this.lastPongTime}`);
                this.disconnectConnection(SocketConnections.order);
                this.connect(SocketConnections.order);
                clearInterval(this.observerInterval);
                this.observerInterval = null;
                return;
            }
            const pingRequest: SocketGenericMessage = {
                command: SocketCommands.ping,
            };
            this.lastPingTime = Date.now();
            webSocketBridge.send(pingRequest);
        }, (this.websocketTimeout / 2) - 1e3);
    }

    async connect(connection: SocketConnections) {
        if (this.webSocketBridges[connection]) {
            console.log('Socket Already connected not sending request', connection);
            return;
        }
        console.log('Connecting to socket', connection);

        const channelObserver: Subject<any> = new Subject();

        const webSocketBridge = new WebSocketBridge();
        this.webSocketBridges[connection] = webSocketBridge;

        this.connectionQueueMap[connection] = {
            isConnectionOpened: false,
            messages: []
        } as SocketMessageQueue;

        await this._connectAndAuthorizeSocket(webSocketBridge, connection, channelObserver);

        webSocketBridge.listen((message, _) => {

            // Authentication response has the server configued time-out in it.
            // Will start observing timeout according to that time.
            if (message && message.message === WebsocketMessages.AuthenticationSuccess) {
                this.websocketTimeout = message.timeout * 1000;
                this.lastPongTime = Date.now();
                this.observeWebsocketDisconnect(webSocketBridge);
            }
        })

        const streamMap = new Map<StreamTypes, Subject<any>>();
        for (const StreamType in StreamTypes) {
            if (StreamType) {
                streamMap[StreamTypes[StreamType]] = new Subject();
                this.webSocketBridges[connection].demultiplex(StreamTypes[StreamType], (action: SocketAction, stream) => {
                    // console.log('listen', action, stream);
                    streamMap[stream].next(action);
                    const orderBookData = action.order_book_data;
                    const tradeBookData = action.trade_book_data;
                    const positionsData = action.positions_data;
                    const rejectionData = action.rejections_data;
                    const holdingsData = action.holdings_data;
                    const broadCastData = action.broadcast_messages;
                    const alertsData = action.alerts_data;
                    const command = action.command;
                    const productConversionSummary = action.product_conversions_data;
                    const mapping = action.mapping;
                    const kyc_status = action.kyc_status;
                    const dms_connection_status = action.dms_connection_status;
                    const funds_status = action.funds_status;
                    const foBannedSymbols = action.fo_ban;
                    const mutualFundData = action.mutual_fund_holdings_data;
                    this.lastPongTime = Date.now();

                    this._routeSubject(SockRoutes.rejection).next(rejectionData);
                    this._routeSubject(SockRoutes.orderbook).next(orderBookData);
                    this._routeSubject(SockRoutes.tradebook).next(tradeBookData);
                    this._routeSubject(SockRoutes.postions).next(positionsData);
                    this._routeSubject(SockRoutes.holding).next(holdingsData);
                    this._routeSubject(SockRoutes.broadcastMessages).next(broadCastData);
                    this._routeSubject(SockRoutes.alertsData).next(alertsData);
                    this._routeSubject(SockRoutes.commands).next(command);
                    this._routeSubject(SockRoutes.productConversionSummary).next(productConversionSummary);
                    this._routeSubject(SockRoutes.mapping).next(mapping);
                    this._routeSubject(SockRoutes.kyc_status).next(kyc_status);
                    this._routeSubject(SockRoutes.dms_connection_status).next(dms_connection_status);
                    this._routeSubject(SockRoutes.funds_status).next(funds_status);
                    this._routeSubject(SockRoutes.fo_ban).next(foBannedSymbols);
                    this._routeSubject(SockRoutes.mutualFund).next(mutualFundData);
                });
                this.streams[connection] = streamMap;
            }
        }

        this.channelObservers[connection] = channelObserver;
        return channelObserver;
    }

    connectToMarketFeed(connection: SocketConnections) {
        if (this.webSocketBridges[connection]) {
            console.log('Socket Already connected not sending request', connection);
            return;
        }
        console.log('Connecting to socket', connection);

        const channelObserver: Subject<any> = new Subject();

        const webSocketBridge = new WebSocketBridge();
        this.webSocketBridges[connection] = webSocketBridge;

        this.connectionQueueMap[connection] = {
            isConnectionOpened: false,
            messages: []
        } as SocketMessageQueue;

        this._connectAndAuthorizeSocket(webSocketBridge, connection, channelObserver);

        const streamMap = new Map<MarketStreams, Subject<any>>();
        for (const streamType in MarketStreams) {
            if (streamType) {
                streamMap[MarketStreams[streamType]] = new Subject();
                this.webSocketBridges[connection].demultiplex(MarketStreams[streamType], (action: SocketAction, stream) => {
                    // console.log('listen', action, stream);
                    streamMap[stream].next(action);
                    const orderDepth = action.depth;
                    const quotesData = action.quote;
                    const openInterest = action.oi;
                    const dpr = action.dpr;
                    const indices = action.indices;
                    const marketSession = action.session;
                    const notices = action.notice;
                    this._routeSubject(SockRoutes.orderdepth).next(orderDepth);
                    this._routeSubject(SockRoutes.marketwatch).next(quotesData);
                    this._routeSubject(SockRoutes.openInterest).next(openInterest);
                    this._routeSubject(SockRoutes.dpr).next(dpr);
                    this._routeSubject(SockRoutes.indices).next(indices);
                    this._routeSubject(SockRoutes.marketSession).next(marketSession);
                    this._routeSubject(SockRoutes.notices).next(notices);

                });

                this.streams[connection] = streamMap;
            }
        }

        this.channelObservers[connection] = channelObserver;
        return channelObserver;
    }

    private async _connectAndAuthorizeSocket(webSocketBridge, connection, channelObserver) {
        let authResponse;
        let token;

        authResponse = await this.authenticateSocket();
        token = authResponse.token;
        if (connection === SocketConnections.order) {
            webSocketBridge.connect(applicationUrls.SOCKET_URL + connection);
        } else if (connection === SocketConnections.marketData) {
            webSocketBridge.connect(applicationUrls.MARKET_DATA_URL, 'http-only');
        }

        webSocketBridge.socket.addEventListener('open', () => {
            const authRequest: SocketGenericMessage = {
                command: SocketCommands.authenticate,
                token
            };
            const a = this.webSocketBridges[connection].send(authRequest);
            this.connectionQueueMap[connection].isConnectionOpened = true;
            this._eventService.publish(EVENTS.SOCKET_AUTHENTICATED, this.firstConnection);
            if (this.firstConnection) {
                this.firstConnection = !this.firstConnection;
            }

            // Sending event whenever market data socket is connected.
            if (connection === SocketConnections.marketData) {
                this._eventService.publish(IONIC_EVENTS.socketConnected, true);
            }

            const messages = this.connectionQueueMap[connection].messages;

            while (messages.length) {
                this.send(connection, messages.shift());
            }
            this._resubscribe(connection);
        });

        webSocketBridge.socket.addEventListener('close', () => {
            this.connectionQueueMap[connection].isConnectionOpened = false;
        });

        webSocketBridge.listen(async (action, stream) => {
            // If Stream = null then action is Socket Message
            if (stream === null) {
                /**
                 * Publish to Channel Subscribers
                 * only if Stream is null
                 */
                channelObserver.next(action);
            }

            // Listen for Authentication Errors
            const sockError = action as SocketError;
            if (sockError.error &&
                (sockError.code === SocketErrorCodes.InvalidSocket || sockError.code === SocketErrorCodes.SocketAuthentication)) {
                const authResponseReconnect = await this.authenticateSocket();
                console.log('AUTH RESPONSE', authResponseReconnect.token);
                const refreshToken = authResponseReconnect.token;
                /**
                 * If Socket Connection is Interrupted,
                 * we need to reAuthenticate and reConnect
                 */
                console.log('Reconnecting');
                // _webSocketBridge.socket.close();
                const message: SocketGenericMessage = {
                    command: SocketCommands.authenticate,
                    token: refreshToken
                };
                this.webSocketBridges[connection].send(message);
                this._resubscribe(connection);
            }

            if (sockError.error && sockError.error === SocketErrors.invalidTokenError) {
                this.storageHelper.clear();
                // this._events.publish(IONIC_EVENTS.goToLoginPage);
            }
        });
    }

    private _resubscribe(connection: SocketConnections) {
        const previousSubscription = this.lastSubscriptionMap.get(connection);
        this.lastSubscriptionMap.set(connection, []);
        console.log(`Resubscribing ${previousSubscription.length} symbols`);
        if (previousSubscription.length) {
            previousSubscription.forEach(subscription => {
                this.send(connection, subscription);
            });
        }
    }

    /**
     * Listen to Socket Connection's Direct Feed
     * @param connection Socket Connection Path
     */
    getConnectionFeed(connection: SocketConnections): Subject<any> {
        return this.channelObservers[connection];
    }

    /**
     * Listen to Stream Feed
     * @param connection Socket Connection Path
     * @param streamType Stream to Listen
     */
    getStreamFeed(connection: SocketConnections, streamType: StreamTypes): Subject<any> {
        return this.streams[connection][streamType];
    }

    getFeed(connection: SocketConnections, route: SockRoutes) {
        return this._routeSubject(route);
    }


    getMargin() {
        this._marginService.getMarginSummaryData({}).subscribe(res => {
            this.marginSubject.next(res);
        }, () => {
            this.getMarginTerminate();
        });
    }

    /**
     * RT-8610 margin update removed from ZMQ socket, so updates will rely on polling
     * In case of large number of users the ZMQ becomes extremly busy with margin updates
     * and as a result, other updates are delayed by an unacceptable time.
     */

    /**
     * RT-8715 To stop multiple margin calls, We are checking if this.callSubscribed issubscribed 
     * once, then don't subscribe again
     */
    getMarginInit() {
        this.marginCallSubscribe++;
        if (this.marginCallSubscribe > 1) {
            return;
        }
        this.intervalSet = MARGIN_SUMMARY_INTERVAL;
        // Unsetting interval to allow and execute interval changes from server.
        this.cacheService.getConfigParams().then(config => {
            this.intervalSet = config.margin_fetch_interval;
            this.getMarginTerminate();
            /** to update interval from config params, we need to terminate it first and at termination,
             *  we are decrementing, So need to increment once more to compensate for it
            */
            this.marginCallSubscribe++;
            this.setInterval(this.intervalSet);
        });
        this.getMargin();
        this.setInterval(this.intervalSet);
    }

    setInterval(interval) {
        this.interval = setInterval(() => {
            this.getMargin();
        }, interval);
    }


    /**
     * RT-8715 To stop multiple margin calls, We are checking if this.marginCallSubscribe = 0 , which means
     * no active subscription, then clear the interval.
     */

    getMarginTerminate() {
        this.marginCallSubscribe--;
        this.marginCallSubscribe = this.marginCallSubscribe < 0 ? 0 : this.marginCallSubscribe;
        if (this.marginCallSubscribe === 0) {
            clearInterval(this.interval);
        }
    }

    /**
     * Send Message to Socket Connection
     * @param connection Socket Connection Path
     * @param message Message to Send
     */
    send(connection: SocketConnections, message: SocketGenericMessage) {
        const connectionQueue = this.connectionQueueMap[connection];
        if (!connectionQueue.isConnectionOpened) {
            connectionQueue.messages.push(message);
        } else {
            if (message.command.split('-')[0] === SocketCommands.subscribe) {
                this.lastSubscriptionMap.get(connection).push(message);
            }
            this.webSocketBridges[connection].send(message);
        }
    }

    /**
     * Send Message to Stream
     * @param connection Socket Connection Path
     * @param to Stream to Engage
     * @param message Message to Send
     */
    sendToStream(connection: SocketConnections, to: StreamTypes, message: any) {
        this.webSocketBridges[connection].stream(to).send(message);
    }


    disconnectAllSockets() {
        for (const connection in this.webSocketBridges) {
            this.disconnectConnection(connection as SocketConnections);
        }
    }


    disconnectConnection(connection: SocketConnections) {
        if (this.webSocketBridges[connection] && this.webSocketBridges[connection].socket) {
            this.webSocketBridges[connection].socket.close(1000, '', { keepClosed: true });
            // Remove Connection Reference
            delete this.webSocketBridges[connection];
        }
    }

    isConnected(connection: SocketConnections) {
        try {
            return this.connectionQueueMap[connection].isConnectionOpened;
        } catch {
            return false;
        }
    }

    setEventService(eventService: EventsService) {
        this._eventService = eventService;
    }
}
